// src/lib/kinema/devtools/StateInspector.tsx
import { useState } from 'react';
import { useLayer, useResolvedLayer, useSelection, usePlayhead } from '../konva/hooks';
import type { Effect, Keyframe } from '../core/types';

export function StateInspector() {
  const { primary } = useSelection();
  const layer = useLayer(primary);
  const resolved = useResolvedLayer(primary);
  const { time, frameRate } = usePlayhead();

  if (!layer || !resolved) {
    return (
      <div style={{ padding: 8, fontSize: 12, color: '#94a3b8', background: '#ffffff', height: '100%' }}>
        Select a layer to inspect
      </div>
    );
  }

  const props = Object.keys(layer).filter((k) => k !== 'effects' && k !== 'id' && k !== 'type' && k !== 'name');

  return (
    <div style={{ padding: 8, fontSize: 12, color: '#334155', background: '#ffffff', height: '100%' }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        <span style={{ color: '#3b82f6' }}>{layer.type}</span> "{layer.name}"
        <span style={{ color: '#94a3b8', fontWeight: 400 }}> @ t={time.toFixed(2)}s</span>
      </div>

      {/* Properties table */}
      <table style={{ fontFamily: 'monospace', fontSize: 11, borderCollapse: 'collapse', width: '100%' }}>
        <tbody>
          {props.map((key) => {
            const base = (layer as Record<string, unknown>)[key];
            const computed = (resolved as Record<string, unknown>)[key];
            const isAnimated = base !== computed;
            return (
              <tr key={key} style={{ borderBottom: '1px solid #f8fafc' }}>
                <td style={{ padding: '2px 8px 2px 0', color: '#64748b', width: 100 }}>{key}</td>
                <td style={{ padding: '2px 8px', color: isAnimated ? '#2563eb' : '#334155', fontWeight: isAnimated ? 600 : 400 }}>
                  {formatValue(computed)}
                </td>
                {isAnimated && (
                  <td style={{ padding: '2px 8px', color: '#94a3b8', fontSize: 10 }}>
                    base: {formatValue(base)}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Effects with full keyframe details */}
      {Object.keys(layer.effects).length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 600, color: '#64748b', marginBottom: 6, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Effects
          </div>
          {Object.values(layer.effects).map((effect) => (
            <EffectDetail key={effect.id} effect={effect} frameRate={frameRate} />
          ))}
        </div>
      )}
    </div>
  );
}

function EffectDetail({ effect, frameRate }: { effect: Effect; frameRate: number }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div style={{ marginBottom: 8, background: '#f8fafc', borderRadius: 4, padding: 6 }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}
      >
        <span style={{ color: '#64748b', fontSize: 9 }}>{expanded ? '\u25BC' : '\u25B6'}</span>
        <span style={{ fontWeight: 600, color: '#334155' }}>{effect.property}</span>
        <span style={{ color: '#94a3b8' }}>{effect.keyframes.length} keyframes</span>
        <span style={{ color: effect.enabled ? '#16a34a' : '#ef4444', marginLeft: 'auto', fontSize: 10 }}>
          {effect.enabled ? 'ON' : 'OFF'}
        </span>
      </div>

      {expanded && (
        <table style={{ fontFamily: 'monospace', fontSize: 10, borderCollapse: 'collapse', width: '100%', marginTop: 4 }}>
          <thead>
            <tr style={{ color: '#94a3b8', borderBottom: '1px solid #e2e8f0' }}>
              <th style={{ textAlign: 'left', padding: '2px 6px', fontWeight: 500 }}>Time</th>
              <th style={{ textAlign: 'left', padding: '2px 6px', fontWeight: 500 }}>Frame</th>
              <th style={{ textAlign: 'left', padding: '2px 6px', fontWeight: 500 }}>Value</th>
              <th style={{ textAlign: 'left', padding: '2px 6px', fontWeight: 500 }}>Easing</th>
            </tr>
          </thead>
          <tbody>
            {effect.keyframes.map((kf: Keyframe<unknown>, i: number) => (
              <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '2px 6px', color: '#334155' }}>{kf.time.toFixed(3)}s</td>
                <td style={{ padding: '2px 6px', color: '#64748b' }}>f{Math.round(kf.time * frameRate)}</td>
                <td style={{ padding: '2px 6px', color: '#2563eb' }}>{formatValue(kf.value)}</td>
                <td style={{ padding: '2px 6px', color: '#94a3b8' }}>{kf.easing}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function formatValue(v: unknown): string {
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toFixed(3);
  if (typeof v === 'string') return v.length > 30 ? v.slice(0, 30) + '...' : v;
  return JSON.stringify(v);
}
